import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

export const TablePagination = ({
    count,
    rowsPerPageOptions,
    currentPage,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    loading,
}: {
    count: number;
    rowsPerPageOptions: number[];
    currentPage: number;
    rowsPerPage: number;
    loading: boolean;
    onPageChange: (newPage: number) => void;
    onRowsPerPageChange: (newPageSize: number) => void;
}) => {
    const numberOfPages = Math.ceil(count / rowsPerPage);

    const handlePageInput = (pageNumber: number) => {
        if (pageNumber > 0 && pageNumber <= numberOfPages) {
            onPageChange(pageNumber);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                paddingLeft: '32px',
                paddingRight: '16px',
                alignItems: 'center',
                gap: 'var(--M-m-compact, 16px)',
                alignSelf: 'stretch',
                border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                background: 'var(--grey-palette-white, #FFF)',
                borderBottomLeftRadius: '14px',
                borderBottomRightRadius: '14px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flex: '1 0 0',
                }}
            >
                <RowsPerPageDropdown
                    onRowsPerPageChange={onRowsPerPageChange}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 'var(--M-m-regular, 24px)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 'var(--M-m-regular, 24px)',
                        }}
                    >
                        <ResultCounter count={count} currentPage={currentPage} rowsPerPage={rowsPerPage} />
                        <Paginator
                            handlePageInput={handlePageInput}
                            currentPage={currentPage}
                            numberOfPages={numberOfPages}
                            loading={loading}
                        />
                    </Box>
                    <GoToPageDropdown
                        numberOfPages={numberOfPages}
                        currentPage={currentPage}
                        handlePageInput={handlePageInput}
                    />
                </Box>
            </Box>
        </Box>
    );
};

interface PaginatorProps {
    handlePageInput: (newPage: number) => void;
    currentPage: number;
    numberOfPages: number;
    loading: boolean;
}

const Paginator = ({ handlePageInput, currentPage, numberOfPages, loading }: PaginatorProps) => {
    const handleBackButtonClick = () => {
        handlePageInput(currentPage - 1);
    };

    const handleNextButtonClick = () => {
        handlePageInput(currentPage + 1);
    };

    const DotDotDot = () => (
        <Typography
            sx={{
                color: 'var(--grey-palette-black, #23323F)',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: 500,
                padding: '11px 7px',
                lineHeight: '21px /* 150% */',
            }}
        >
            ...
        </Typography>
    );

    const shouldPageNumberBeDisplayed = (pageNumber: number) => {
        if (pageNumber === 1) return false;
        if (pageNumber === numberOfPages) return false;

        if (currentPage <= 2 && pageNumber <= 3) return true;
        if (currentPage >= numberOfPages - 1 && pageNumber >= numberOfPages - 2) return true;
        if (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1) return true;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--S-s-regular, 8px)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    width: '32px',
                    height: '32px',
                    padding: '11px 7px',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: 'var(--radius-md, 8px)',
                    cursor: 'pointer',
                }}
                onClick={handleBackButtonClick}
            >
                <ChevronLeft
                    size={20}
                    style={{
                        color: 'var(--grey-palette-grey-5, #6B7985)',
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--S-s-regular, 8px)',
                }}
            >
                <PaginationNumber
                    loading={loading}
                    pageNumber={1}
                    currentPage={currentPage}
                    handlePageInput={handlePageInput}
                />
                {currentPage > 3 && numberOfPages > 4 && <DotDotDot />}
                {Array.from({ length: numberOfPages }, (_, index) => {
                    const pageNumber = index + 1;

                    if (shouldPageNumberBeDisplayed(pageNumber)) {
                        return (
                            <PaginationNumber
                                loading={loading}
                                key={index}
                                pageNumber={pageNumber}
                                currentPage={currentPage}
                                handlePageInput={handlePageInput}
                            />
                        );
                    }
                    return null;
                })}
                {currentPage < numberOfPages - 1 && numberOfPages > 3 && <DotDotDot />}
                {numberOfPages !== 1 && (
                    <PaginationNumber
                        loading={loading}
                        pageNumber={numberOfPages}
                        currentPage={currentPage}
                        handlePageInput={handlePageInput}
                    />
                )}
                <Box
                    sx={{
                        display: 'flex',
                        width: '32px',
                        height: '32px',
                        padding: '11px 7px',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        borderRadius: 'var(--radius-md, 8px)',
                        cursor: 'pointer',
                    }}
                    onClick={handleNextButtonClick}
                >
                    <ChevronRight
                        size={20}
                        style={{
                            color: 'var(--grey-palette-grey-5, #6B7985)',
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

const ResultCounter = ({
    count,
    currentPage,
    rowsPerPage,
}: {
    count: number;
    currentPage: number;
    rowsPerPage: number;
}) => {
    return (
        <Typography
            sx={{
                color: 'var(--grey-palette-grey-5, #6B7985)',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '21px /* 150% */',
            }}
        >
            {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, count)} of {count}
        </Typography>
    );
};

const GoToPageDropdown = ({
    numberOfPages,
    currentPage,
    handlePageInput,
}: {
    numberOfPages: number;
    currentPage: number;
    handlePageInput: (newPage: number) => void;
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 'var(--M-m-regular, 24px)',
            }}
        >
            <Typography
                sx={{
                    color: 'var(--grey-palette-grey-5, #6B7985)',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '21px /* 150% */',
                }}
            >
                Go to page
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--S-s-regular, 8px)',
                }}
            >
                <Select
                    value={currentPage}
                    onChange={(event: any) => handlePageInput(event.target.value)}
                    inputProps={{ 'aria-label': 'Page number' }}
                    autoWidth
                    MenuProps={{
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                    }}
                    IconComponent={() => (
                        <ChevronDown
                            style={{
                                pointerEvents: 'none',
                                position: 'absolute',
                                right: '10px',
                            }}
                            size={16}
                            stroke="#6B7985"
                        />
                    )}
                    sx={{
                        boxShadow: 'none',
                        '.MuiOutlinedInput-notchedOutline': { border: 0 },
                        '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            border: 0,
                        },
                        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 0,
                        },
                        '& .MuiSelect-select': {
                            padding: 0,
                        },
                    }}
                >
                    {Array.from({ length: numberOfPages }, (_, index) => (
                        <MenuItem key={index} value={index + 1}>
                            {index + 1}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        </Box>
    );
};

const RowsPerPageDropdown = ({
    rowsPerPage,
    rowsPerPageOptions,
    onRowsPerPageChange,
}: {
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    onRowsPerPageChange: (newPageSize: number) => void;
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--S-s-regular, 8px)',
            }}
        >
            <Typography
                sx={{
                    color: 'var(--grey-palette-grey-5, #6B7985)',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '21px /* 150% */',
                }}
            >
                Rows per page:
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Select
                    value={rowsPerPage}
                    onChange={(event: any) => onRowsPerPageChange(event.target.value)}
                    inputProps={{ 'aria-label': 'Rows per page' }}
                    autoWidth
                    MenuProps={{
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                    }}
                    IconComponent={() => (
                        <ChevronDown
                            style={{
                                pointerEvents: 'none',
                                position: 'absolute',
                                right: '10px',
                            }}
                            size={16}
                            stroke="#6B7985"
                        />
                    )}
                    sx={{
                        boxShadow: 'none',
                        '.MuiOutlinedInput-notchedOutline': { border: 0 },
                        '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            border: 0,
                        },
                        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 0,
                        },
                        '& .MuiSelect-select': {
                            padding: 0,
                        },
                    }}
                >
                    {rowsPerPageOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        </Box>
    );
};

const PaginationNumber = ({
    pageNumber,
    currentPage,
    handlePageInput,
    loading,
}: {
    pageNumber: number;
    currentPage: number;
    handlePageInput: (newPage: number) => void;
    loading: boolean;
}) => {
    const selected = pageNumber === currentPage;
    return (
        <Button
            disabled={loading}
            onClick={() => handlePageInput(pageNumber)}
            sx={{
                display: 'flex',
                maxWidth: '32px',
                minWidth: '32px',
                maxHeight: '32px',
                mindHeight: '32px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--S-s-regular, 8px)',
                borderRadius: 'var(--radius-sm, 6px)',
                background: selected ? 'var(--grey-palette-grey-6, #3A4B59)' : 'transparent',
                cursor: 'pointer',
                ':hover': {
                    background: selected
                        ? 'var(--grey-palette-grey-6, #3A4B59)'
                        : 'var(--grey-palette-grey-1, #F7F8FA)',
                },
            }}
        >
            <Typography
                key={pageNumber}
                sx={{
                    color: selected ? 'var(--grey-palette-white, #FFF)' : 'var(--grey-palette-black, #23323F)',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: 500,
                    padding: '11px 7px',
                    lineHeight: '21px /* 150% */',
                }}
            >
                {pageNumber}
            </Typography>
        </Button>
    );
};
